import type { ZodSchema } from 'zod'
import type { FunnelStepComponent, FunnelStepTerminalComponent } from './types'

export type ConfigConditions = Record<string, boolean>
export type StepConfig<T extends ConfigConditions = ConfigConditions> =
  FunnelStepComponent | ConfigTypeStep | ConfigTypeGroup<T> | ConfigTypeTerminal<T>

export type ConfigTypeStep = {
  type: 'step'
  path: string
  schema: ZodSchema
  render: FunnelStepComponent
}

type ConfigTypeGroup<T extends ConfigConditions = ConfigConditions> = {
  type: 'group'
  condition: keyof T
  then: Steps<T>
}

type ConfigTypeTerminal<T extends ConfigConditions = ConfigConditions> = {
  type: 'terminal'
  condition?: keyof T
  render: FunnelStepTerminalComponent
}

export type Steps<T extends ConfigConditions> = [StepConfig<T>, ...StepConfig<T>[]]

export type FlattenedStep =
  | { type: 'step'; path: string; schema: ZodSchema; render: FunnelStepComponent }
  | { type: 'terminal'; path: string; render: FunnelStepTerminalComponent }

export const getFlattenSteps = <T extends ConfigConditions>(
  steps: Steps<T>,
  conditions: T = {} as T
): Array<FlattenedStep> => {
  const result: FlattenedStep[] = []

  for (const step of steps) {
    if (typeof step === 'function') {
      result.push({ type: 'step', path: step.path, schema: step.schema, render: step })
      continue
    }

    if (step.type === 'terminal' && (!step.condition || conditions[step.condition])) {
      result.push({ type: 'terminal', render: step.render, path: step.render.path })
      break
    }

    if (step.type === 'step') {
      result.push(step)
      continue
    }

    if (step.type === 'group' && conditions[step.condition]) {
      result.push(...getFlattenSteps(step.then))
    }
  }

  return result
}
